import React, { useState } from "react";
import PropTypes from "prop-types";
import { Camera } from "lucide-react";

/**
 * Fix over-encoded S3 URLs (e.g., %252540 → %2540 → %40 → @)
 */
const decodeS3Url = (url) => {
    if (!url || typeof url !== 'string') return url;
    let decoded = url;
    let prev = '';
    while (decoded !== prev && decoded.includes('%25')) {
        prev = decoded;
        try {
            decoded = decodeURIComponent(decoded);
        } catch (e) {
            break;
        }
    }
    return decoded;
};

/**
 * ListingCard - A clickable card that displays a property listing preview
 * Used in MoreFromAgent section and AgentPortfolioPage
 */
const ListingCard = ({ listing, className = "" }) => {
    const [imageError, setImageError] = useState(false);

    const {
        slug,
        heroImageUrl,
        heroThumbnailUrl,
        address,
        price,
        bedrooms,
        bathrooms,
        squareFeet,
        photoCount = 0
    } = listing;

    // Use thumbnail if available, fallback to hero image, decode S3 URLs
    const rawImageUrl = heroThumbnailUrl || heroImageUrl;
    const imageUrl = decodeS3Url(rawImageUrl);

    // Build the gallery URL
    const galleryUrl = slug ? `/flow/${slug}` : null;

    // Format price for display
    const formatPrice = (priceValue) => {
        if (!priceValue) return "Price on Request";
        // If already formatted as string with $ sign, return as-is
        if (typeof priceValue === "string" && priceValue.includes("$")) {
            return priceValue;
        }
        // Format number as currency
        const numericPrice = typeof priceValue === "string"
            ? parseInt(priceValue.replace(/[^0-9]/g, ""), 10)
            : priceValue;
        if (isNaN(numericPrice)) return "Price on Request";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(numericPrice);
    };

    // Build property details string (e.g., "4 bed · 3.5 bath · 3,200 sqft")
    const buildDetailsString = () => {
        const parts = [];
        if (bedrooms) parts.push(`${bedrooms} bed`);
        if (bathrooms) parts.push(`${bathrooms} bath`);
        if (squareFeet) parts.push(`${squareFeet.toLocaleString()} sqft`);
        return parts.join(" · ");
    };

    const CardContent = () => (
        <>
            {/* Hero Image */}
            <div className="aspect-[4/3] relative overflow-hidden bg-slate-200">
                {imageUrl && !imageError ? (
                    <img
                        src={imageUrl}
                        alt={address || "Property"}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                        <Camera className="h-8 w-8 text-slate-400" />
                    </div>
                )}
                {/* Photo Count Badge */}
                {photoCount > 0 && (
                    <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded text-sm">
                        <Camera className="h-3.5 w-3.5" />
                        <span>{photoCount}</span>
                    </div>
                )}
            </div>

            {/* Details - min-h ensures consistent card heights */}
            <div className="p-4 min-h-[88px]">
                <p className="font-semibold text-lg text-slate-900 truncate">
                    {formatPrice(price)}
                </p>
                {address && (
                    <p className="text-sm text-slate-600 truncate mt-0.5">
                        {address}
                    </p>
                )}
                {buildDetailsString() && (
                    <p className="text-sm text-slate-500 mt-1">
                        {buildDetailsString()}
                    </p>
                )}
            </div>
        </>
    );

    // If we have a URL, render as a link
    if (galleryUrl) {
        return (
            <a
                href={galleryUrl}
                className={`group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-slate-200 ${className}`}
            >
                <CardContent />
            </a>
        );
    }

    // Otherwise render as a div
    return (
        <div className={`bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200 ${className}`}>
            <CardContent />
        </div>
    );
};

ListingCard.propTypes = {
    listing: PropTypes.shape({
        id: PropTypes.string,
        batchId: PropTypes.string,
        slug: PropTypes.string,
        template: PropTypes.string,
        address: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        bedrooms: PropTypes.number,
        bathrooms: PropTypes.number,
        squareFeet: PropTypes.number,
        heroImageUrl: PropTypes.string,
        heroThumbnailUrl: PropTypes.string,
        photoCount: PropTypes.number,
        publishedAt: PropTypes.string
    }).isRequired,
    className: PropTypes.string
};

export default ListingCard;
